import {
  ButtonGeneral,
  ButtonSecondary,
  DateInputSplit,
  Dropdown,
  DropdownWidth,
  SegmentedControl,
  typographyRem,
} from '@by-miles/bymiles-styles'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import React, { useContext, useState } from 'react'

import { genericDateErrorCopy } from '../../../../../constants'
import { ProviderContext } from '../../../../../Provider'
import { AccessibleFormWrapper } from '../../../../AccessibleFormWrapper'
import { GenericOverlay } from '../../../../Overlays'
import { claimValidationRules } from '../../../utils/claimValidationRules'
import { DateErrorState, DateState } from '../../../utils/dateTypes'
import { handleDropdownState } from '../../../utils/handleDropdownState'
import { handlePrefilledDate } from '../../../utils/handlePrefilledDate'
import { handlePrefilledDropdown } from '../../../utils/handlePrefilledDropdown'
import { AddClaimModalProps, ClaimErrors } from './utils/addClaimTypes'
const { h2: H2, h5: H5 } = typographyRem
dayjs.extend(utc)

export const AddClaimModal = ({
  claims,
  setClaims,
  updatedDriverDetails,
  setUpdatedDriverDetails,
  updatedAdditionalDriver,
  setUpdatedAdditionalDriver,
  isEdit,
  setIsEditClaim,
  index,
  setSelectedClaimIndex,
  claimsData,
}: AddClaimModalProps) => {
  const {
    state: { isMobile, selectedDriverIndex },
    handleCloseModal,
  } = useContext(ProviderContext)

  const [incidentType, setIncidentType] = useState<string>(isEdit ? claims[index].code : '')
  const [incidentDate, setIncidentDate] = useState<DateState>(
    isEdit ? handlePrefilledDate(claims[index].date) : handlePrefilledDate(''),
  )
  const [atFault, setAtFault] = useState<boolean | undefined>(isEdit ? claims[index].at_fault : undefined)
  const [loseNoClaims, setLoseNoClaims] = useState<boolean | undefined>(isEdit ? claims[index].ncd_lost : undefined)

  const [errors, setErrors] = useState<ClaimErrors>({
    incidentError: '',
    atFaultError: '',
    loseNoClaimsError: '',
  })
  const [dateError, setDateError] = useState<boolean>(false)
  const [customDateError, setCustomDateError] = useState<string>('')

  const handleCheckDate = () => {
    if (!incidentDate.month || !incidentDate.year) {
      return
    } else if (dateError) {
      setCustomDateError(genericDateErrorCopy)
    }
    if (incidentDate.year.toString()?.length === 4) {
      const month = incidentDate.month
      const year = incidentDate.year
      const d = new Date(year, month)
      const date = dayjs(d)
      if (date && date.isAfter(dayjs().add(1, 'M'))) {
        setCustomDateError("That's odd, the date you have selected is in the future. Please check and try again.")
        return false
      } else if (date && date.isBefore(dayjs().subtract(5, 'year'))) {
        setCustomDateError(
          "Provided this is the right date, you don't need to declare claims or incidents older than five years old, so you can cancel this incident.",
        )
      }
    }
  }

  const handleDobErrors = (errors: DateErrorState) => {
    if (errors.month || errors.year) {
      setDateError(true)
    }
  }

  const validateClaim = () => {
    const dateValid = incidentDate.month && incidentDate.year && !customDateError
    const error = 'Please answer this question to continue'
    if (!dateValid || !incidentType || typeof atFault !== 'boolean' || typeof loseNoClaims !== 'boolean') {
      if (!dateValid) {
        setCustomDateError(!incidentDate.month && !incidentDate.year ? error : genericDateErrorCopy)
      }
      setErrors({
        incidentError: incidentType ? '' : error,
        atFaultError: typeof atFault === 'boolean' ? '' : error,
        loseNoClaimsError: typeof loseNoClaims === 'boolean' ? '' : error,
      })
      return false
    }
    return true
  }

  const handleAddIncident = () => {
    const isValid = validateClaim()
    if (isValid) {
      const date = dayjs.utc(`${incidentDate?.year}/${incidentDate?.month}`).toISOString()
      const updatedClaims = claims || []
      const claim = {
        code: incidentType,
        at_fault: atFault,
        ncd_lost: loseNoClaims,
        date,
      }
      if (isEdit) {
        updatedClaims[index] = claim
      } else {
        updatedClaims.push(claim)
      }
      setClaims(updatedClaims)
      !selectedDriverIndex
        ? setUpdatedDriverDetails({ ...updatedDriverDetails, claims: [...updatedClaims] })
        : setUpdatedAdditionalDriver({ ...updatedAdditionalDriver, claims: [...updatedClaims] })
      setIsEditClaim(false)
      handleCloseModal('claims')
    }
  }

  return (
    <GenericOverlay
      onClose={() => {}}
      hideDismissIcon={true}
      overlay={
        <>
          <H2 className="modal-title">{`Claim / incident ${index + 1}.`}</H2>
          <AccessibleFormWrapper onSubmit={handleAddIncident}>
            <div className="question-container">
              <div className="question-text-container">
                <H5>What type of incident was it?</H5>
              </div>
              {claimsData && (
                <Dropdown
                  handleDropdownChange={option => {
                    setIncidentType(option.value)
                    setErrors({ ...errors, incidentError: '' })
                  }}
                  isMobile={isMobile}
                  options={claimsData?.claims || []}
                  preFilled={handlePrefilledDropdown(claimsData?.claims || [], incidentType)}
                  states={handleDropdownState(incidentType, errors.incidentError)}
                  errorMessage={errors.incidentError}
                  width={DropdownWidth.Large}
                  className="dropdown"
                  placeholder="Select"
                  dataCy="incident-type-dropdown-list"
                />
              )}
            </div>
            <div className="question-container" id="customPurchaseDate">
              <div className="question-text-container">
                <H5>When did this incident happen?</H5>
              </div>
              <DateInputSplit
                dataCy="incident-date-text-field"
                showLabels={true}
                validateOnBlur={false}
                toggleErrorMessage={!!customDateError}
                dateStructure={['month', 'year']}
                onBlur={() => handleCheckDate()}
                errorMessage={customDateError}
                onError={handleDobErrors}
                onChange={(value: number, field: string) => {
                  setCustomDateError('')
                  setDateError(false)
                  setIncidentDate({
                    ...incidentDate,
                    [field]: value,
                  })
                }}
                customValidation={claimValidationRules}
                defaultValue={incidentDate}
              />
            </div>
            <div className="question-container">
              <div className="question-text-container">
                <H5>Were you found to be at fault for the incident?</H5>
              </div>
              <SegmentedControl
                options={[
                  { label: 'yes', value: true },
                  { label: 'no', value: false },
                ]}
                name="incident-at-fault"
                preFilledValue={atFault}
                error={errors.atFaultError}
                onClick={value => {
                  if (typeof value === 'boolean') {
                    setAtFault(value)
                    setErrors({ ...errors, atFaultError: '' })
                  }
                }}
                dataCy="incident-at-fault-toggle"
              />
            </div>
            <div className="question-container">
              <div className="question-text-container">
                <H5>Did you lose your No Claims Discount as a result of the incident?</H5>
              </div>
              <SegmentedControl
                options={[
                  { label: 'yes', value: true },
                  { label: 'no', value: false },
                ]}
                name="lose-no-claims"
                preFilledValue={loseNoClaims}
                error={errors.loseNoClaimsError}
                onClick={value => {
                  if (typeof value === 'boolean') {
                    setLoseNoClaims(value)
                    setErrors({ ...errors, loseNoClaimsError: '' })
                  }
                }}
                dataCy="lose-no-claims-toggle"
              />
            </div>
          </AccessibleFormWrapper>
          <div className="additional-modal-button-container">
            <ButtonGeneral
              className="button-add"
              label="Add claim / incident"
              onClick={handleAddIncident}
              dataCy="add-incident-button"
              iconRight="IconCaretRight"
            />
            <ButtonSecondary
              className="button-cancel"
              label="Cancel"
              onClick={() => {
                handleCloseModal('claims')
                setIsEditClaim(false)
                if (claims?.length < 1) {
                  setSelectedClaimIndex(0)
                  document.getElementById('input-has-claims-toggle-no')?.click()
                }
              }}
            />
          </div>
        </>
      }
    />
  )
}
